import { useState, useCallback } from "react";
import { Images, Upload, Download, Trash2, FileText, Loader2, GripVertical, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "What image formats can I convert to PDF?",
    answer: "We support PNG, JPG, JPEG, GIF, BMP, and WebP formats. All common image types work perfectly."
  },
  {
    question: "Can I combine multiple images into one PDF?",
    answer: "Yes, upload multiple images, arrange them in your preferred order, and convert them all into a single PDF."
  },
  {
    question: "Is there a limit on the number of images?",
    answer: "No strict limit. You can combine as many images as needed, though very large batches may take longer to process."
  },
  {
    question: "Will image quality be preserved?",
    answer: "Yes, we maintain the original image quality when converting to PDF."
  },
  {
    question: "Is this service free?",
    answer: "Yes, completely free with no signup required. Files auto-delete after 30 minutes for privacy."
  }
];

const ImageToPdfPage = () => {
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previews, setPreviews] = useState({});

  const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/bmp", "image/webp"];

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const generatePreview = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviews((prev) => ({ ...prev, [file.name]: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter((f) =>
      validTypes.includes(f.type)
    );
    if (droppedFiles.length === 0) {
      toast.error("Please upload valid image files (PNG, JPEG, GIF, BMP, WebP)");
      return;
    }
    droppedFiles.forEach(generatePreview);
    setFiles((prev) => [...prev, ...droppedFiles]);
    setResult(null);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files).filter((f) =>
      validTypes.includes(f.type)
    );
    if (selectedFiles.length === 0) {
      toast.error("Please upload valid image files");
      return;
    }
    selectedFiles.forEach(generatePreview);
    setFiles((prev) => [...prev, ...selectedFiles]);
    setResult(null);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const moveFile = (fromIndex, toIndex) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      const [removed] = newFiles.splice(fromIndex, 1);
      newFiles.splice(toIndex, 0, removed);
      return newFiles;
    });
  };

  const handleConvert = async () => {
    if (files.length === 0) {
      toast.error("Please select at least one image");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await axios.post(`${API}/image-to-pdf`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Conversion failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (result?.files?.length > 0) {
      try {
        const downloadUrl = `${API}/download/${result.files[0].id}`;
        await downloadFile(downloadUrl, result.files[0].name || "converted.pdf");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFiles([]);
    setResult(null);
    setPreviews({});
  };

  return (
    <>
      <SEOHead
        title="Image to PDF Converter Free Online | JPG to PDF"
        description="Convert images to PDF online free. Combine multiple JPG, PNG, or other images into a single PDF. Drag, drop, rearrange. No signup required."
        canonicalPath="/image-to-pdf"
        keywords="image to PDF, JPG to PDF, PNG to PDF, convert images to PDF, photo to PDF, free image converter"
      />
      
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#FF3B30] flex items-center justify-center">
                <Images className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                Image to PDF Converter Free
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Convert JPG, PNG, GIF, and other images into a single PDF document. 
              Drag and drop to rearrange order. No signup required.
            </p>
          </div>

        {/* Upload Area */}
        {!result && (
          <div className="space-y-6 animate-fade-in-up animate-delay-100">
            <div
              className={`upload-zone ${isDragging ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input").click()}
              data-testid="upload-zone"
            >
              <input
                id="file-input"
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                <Upload className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="font-medium text-lg">Drop images here</p>
                <p className="text-sm text-muted-foreground mt-1">
                  PNG, JPEG, GIF, BMP, WebP (max 50MB each)
                </p>
              </div>
            </div>

            {/* Selected Files */}
            {files.length > 0 && (
              <div className="space-y-3" data-testid="selected-files">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">
                    Selected Images ({files.length})
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFiles([])}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid="clear-all-button"
                  >
                    Clear All
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {files.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="relative border border-border hover:border-black transition-colors group"
                      data-testid={`selected-file-${index}`}
                    >
                      <div className="aspect-square bg-secondary overflow-hidden">
                        {previews[file.name] ? (
                          <img
                            src={previews[file.name]}
                            alt={`Uploaded image ${file.name} for PDF conversion`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Images className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="p-2">
                        <p className="text-xs truncate">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(0)} KB
                        </p>
                      </div>
                      <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-white/90 p-1 cursor-grab">
                          <GripVertical className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        className="absolute top-2 right-2 bg-white/90 p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive hover:text-white"
                        data-testid={`remove-file-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-2 right-2 bg-black text-white text-xs px-2 py-0.5">
                        {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  Drag to reorder. First image will be the first page.
                </p>
              </div>
            )}

            {/* Convert Button */}
            <Button
              onClick={handleConvert}
              disabled={files.length === 0 || isProcessing}
              className="w-full h-14 bg-black hover:bg-black/90 text-white rounded-sm btn-hover-lift text-lg font-medium"
              data-testid="convert-button"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating PDF...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5 mr-2" />
                  Create PDF
                </>
              )}
            </Button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6 animate-fade-in-up" data-testid="result-section">
            <div className="border border-border p-8 text-center">
              <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight mb-2">
                PDF Created Successfully
              </h2>
              <p className="text-muted-foreground mb-6">
                {result.files[0]?.filename}
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="rounded-sm"
                  data-testid="reset-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Start Over
                </Button>
                <Button
                  onClick={handleDownload}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                  data-testid="download-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>

            <div className="bg-secondary p-4 text-center">
              <p className="text-sm text-muted-foreground">
                ⏱ File will be automatically deleted in 30 minutes
              </p>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <ToolContentSection toolKey="image-to-pdf" content={toolContent["image-to-pdf"]} />
        <FAQSection faqs={faqs} />

        {/* Related Tools */}
        <RelatedTools currentTool="image-to-pdf" />
      </div>
    </div>
    </>
  );
};

export default ImageToPdfPage;
